const THRESHOLD = 500000;

export function shippingState(total) {
  const amount = Math.max(0, Number(total) || 0);
  return { amount, remaining: Math.max(0, THRESHOLD - amount), progress: Math.min(1, amount / THRESHOLD), qualifies: amount >= THRESHOLD };
}

class ScenturiesCartShipping extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.drawer = this.closest('theme-drawer');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    // Morphing can move/reconnect this element. Keep its painted position;
    // only an actual drawer-open event should replay the entrance from zero.
    this.value ??= 0;
    this.total = null;
    const options = { signal: this.controller.signal };
    this.drawer.addEventListener('theme-drawer:open', () => {
      this.readState(true);
    }, options);
    this.drawer.addEventListener('theme-drawer:close', () => this.stop(), options);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) {
        this.stop();
        if (this.state) this.paint(this.state.progress, this.state.qualifies);
      }
    }, options);
    this.observer = new MutationObserver(() => this.readState());
    this.observer.observe(this.drawer, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['data-shipping-total', 'data-shipping-currency', 'data-shipping-country'] });
    this.readState();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }

  readState(replay = false) {
    const source = this.drawer.querySelector('[data-sc-shipping-state]');
    if (!source) return;
    const enabled = source.dataset.shippingCurrency === 'PKR' && source.dataset.shippingCountry === 'PK';
    this.hidden = !enabled;
    if (!enabled) return this.stop();
    const total = Number(source.dataset.shippingTotal) || 0;
    if (!replay && this.total === total) return;
    this.total = total;
    this.state = shippingState(total);
    const amount = 'PKR ' + new Intl.NumberFormat('en-PK', { maximumFractionDigits: 2 }).format(this.state.remaining / 100);
    this.querySelector('.sc-shipping__message').textContent = this.state.qualifies ? this.dataset.unlocked : this.dataset.remaining.replace('__amount__', amount);
    this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(Math.min(5000, this.state.amount / 100)));
    if (!this.drawer.hasAttribute('open')) {
      this.stop();
      this.paint(this.state.progress, this.state.qualifies);
      return;
    }
    this.moveTo(this.state.progress, replay);
  }

  paint(value, arrived = false) {
    this.value = value;
    this.style.setProperty('--shipping-progress', `${value * 100}%`);
    this.toggleAttribute('data-arrived', arrived);
  }

  stop() {
    cancelAnimationFrame(this.frame);
  }

  moveTo(target, replay) {
    this.stop();
    const from = replay ? 0 : this.value;
    this.paint(from);
    if (this.motion.matches) {
      this.paint(target, this.state.qualifies);
      return;
    }
    const duration = replay ? 1000 : 650;
    let start;
    const step = now => {
      if (!this.isConnected || !this.drawer.hasAttribute('open')) return;
      start ??= now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.paint(from + (target - from) * eased);
      if (t < 1) this.frame = requestAnimationFrame(step);
      else {
        this.paint(target, this.state.qualifies);
      }
    };
    this.frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('sc-cart-shipping')) customElements.define('sc-cart-shipping', ScenturiesCartShipping);

